import { ArrowRightIcon, ImageIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { invoiceTotal } from "./calc";
import { currencies, formatMoney } from "./currency";
import { InvoicePreview } from "./InvoicePreview";
import { createBlankInvoice, createId, createSampleInvoice } from "./sample";
import {
  deleteInvoice,
  getDraft,
  getInvoice,
  listInvoices,
  saveDraft,
  saveInvoice,
} from "./storage";
import type {
  CurrencyCode,
  Invoice,
  InvoiceItem,
  InvoiceTemplate,
  SavedInvoiceSummary,
} from "./types";

type SaveState = "idle" | "saving" | "saved";
type View = "compose" | "ledger";

export function InvoiceEditor() {
  const [invoice, setInvoice] = useState<Invoice>(() => createSampleInvoice());
  const [savedId, setSavedId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [view, setView] = useState<View>("compose");
  const [savedInvoices, setSavedInvoices] = useState<SavedInvoiceSummary[]>([]);
  const importInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    getDraft().then((draft) => {
      if (!cancelled && draft) setInvoice(draft);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSaveState("saving");
    const handle = window.setTimeout(async () => {
      if (savedId) await saveInvoice(invoice);
      else await saveDraft(invoice);
      setSaveState("saved");
      setLastSavedAt(Date.now());
    }, 350);
    return () => window.clearTimeout(handle);
  }, [invoice, savedId]);

  const total = useMemo(() => invoiceTotal(invoice), [invoice]);

  async function handleCommit() {
    const saved = await saveInvoice(invoice);
    setInvoice(saved);
    setSavedId(saved.id);
    setSaveState("saved");
    setLastSavedAt(Date.now());
  }

  async function refreshSavedInvoices() {
    setSavedInvoices(await listInvoices());
  }

  async function openLedger() {
    await refreshSavedInvoices();
    setView("ledger");
  }

  function handleNewBlank() {
    setInvoice(createBlankInvoice());
    setSavedId(null);
    setView("compose");
  }

  function exportInvoice() {
    const blob = new Blob([JSON.stringify(invoice, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${invoice.details.invoiceNumber || "invoice"}.nvoice`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function importInvoice(file: File | undefined) {
    if (!file) return;
    const text = await file.text();
    const parsed = JSON.parse(text) as Invoice;
    setInvoice({ ...parsed, id: parsed.id || createId(), updatedAt: new Date().toISOString() });
    setSavedId(null);
    setView("compose");
  }

  const update = (recipe: (invoice: Invoice) => Invoice) =>
    setInvoice((current) => ({ ...recipe(current), updatedAt: new Date().toISOString() }));

  return (
    <div className="editor-chrome min-h-screen bg-paper p-3 text-ink md:p-6">
      <div className="mx-auto max-w-[1400px] border border-rule bg-page">
        <CompactHeader
          view={view}
          setView={setView}
          invoice={invoice}
          saveState={saveState}
          savedId={savedId}
          lastSavedAt={lastSavedAt}
          onLedger={() => void openLedger()}
          onNew={handleNewBlank}
          onCommit={handleCommit}
          onImport={() => importInputRef.current?.click()}
          onExport={exportInvoice}
          onPrint={() => window.print()}
        />

        {view === "compose" ? (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(520px,640px)_minmax(0,1fr)] lg:items-start">
            <div className="lg:max-h-[calc(100vh-160px)] lg:overflow-y-auto">
              <Composer invoice={invoice} total={total} onChange={update} />
            </div>
            <PreviewFrame invoice={invoice} total={total} />
          </div>
        ) : (
          <Ledger
            invoices={savedInvoices}
            currency={invoice.details.currency}
            onLoad={async (summary) => {
              const stored = await getInvoice(summary.id);
              if (stored) {
                setInvoice(stored);
                setSavedId(stored.id);
                setView("compose");
              }
            }}
            onDuplicate={async (summary) => {
              const stored = await getInvoice(summary.id);
              if (stored) {
                setInvoice({
                  ...stored,
                  id: createId(),
                  details: {
                    ...stored.details,
                    invoiceNumber: `${stored.details.invoiceNumber}-copy`,
                  },
                });
                setSavedId(null);
                setView("compose");
              }
            }}
            onDelete={async (summary) => {
              await deleteInvoice(summary.id);
              await refreshSavedInvoices();
            }}
          />
        )}

        <Footer />
      </div>

      <input
        ref={importInputRef}
        type="file"
        accept=".nvoice,.json,application/json"
        className="hidden"
        onChange={(event) => void importInvoice(event.currentTarget.files?.[0])}
      />
    </div>
  );
}

/* ─────────────── Compact header (2 rows) ─────────────── */

function CompactHeader({
  view,
  setView,
  invoice,
  saveState,
  savedId,
  lastSavedAt,
  onLedger,
  onNew,
  onCommit,
  onImport,
  onExport,
  onPrint,
}: {
  view: View;
  setView: (v: View) => void;
  invoice: Invoice;
  saveState: SaveState;
  savedId: string | null;
  lastSavedAt: number | null;
  onLedger: () => void;
  onNew: () => void;
  onCommit: () => void;
  onImport: () => void;
  onExport: () => void;
  onPrint: () => void;
}) {
  const stateLabel =
    saveState === "saving"
      ? savedId
        ? "Saving"
        : "Drafting"
      : saveState === "saved"
        ? savedId
          ? "Saved"
          : "Draft saved"
        : "Idle";
  const time = lastSavedAt ? humanRelative(lastSavedAt) : "";

  return (
    <header className="no-print">
      {/* Row 1 — wordmark · save status · PRINT */}
      <div className="flex h-11 items-center justify-between gap-4 border-b border-rule px-4 md:px-6">
        <div className="flex items-center gap-3">
          <h1 className="font-semibold text-ink text-[14px] tracking-tight">
            Nvoice<span className="align-super text-[9px]">™</span>
            <span className="ml-1.5 font-normal text-ink-muted">Invoice Generator</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.04em] text-ink-muted md:flex">
            <span
              className={`size-1.5 rounded-full ${saveState === "saving" ? "save-pulse bg-stamp-yellow-edge" : "bg-action"}`}
            />
            <span>{stateLabel}</span>
            {time ? <span className="text-ink-faint">· {time}</span> : null}
          </div>
          <button type="button" className="dsb dsb--primary" onClick={onPrint}>
            Print <ArrowRightIcon className="size-3" />
          </button>
        </div>
      </div>

      {/* Row 2 — tabs + breadcrumb · action toolbar */}
      <div className="flex h-9 items-center justify-between gap-3 border-b border-rule bg-paper px-4 md:px-6">
        <div className="flex items-center gap-3">
          <div className="flex">
            <MiniTab
              label="Compose"
              active={view === "compose"}
              onClick={() => setView("compose")}
              first
            />
            <MiniTab label="Ledger" active={view === "ledger"} onClick={onLedger} />
          </div>
          {view === "compose" ? (
            <span className="font-mono text-[11px] uppercase tracking-[0.04em] text-ink-muted">
              <span className="text-ink-faint">/</span>{" "}
              <span className="font-semibold text-ink">
                {invoice.details.invoiceNumber || "Untitled"}
              </span>
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" className="dsb dsb--xs" onClick={onNew}>
            New
          </button>
          <button type="button" className="dsb dsb--xs" onClick={onCommit}>
            Save copy
          </button>
          <button type="button" className="dsb dsb--xs" onClick={onImport}>
            Import
          </button>
          <button type="button" className="dsb dsb--xs" onClick={onExport}>
            Export
          </button>
        </div>
      </div>
    </header>
  );
}

function MiniTab({
  label,
  active,
  onClick,
  first,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  first?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-6 items-center gap-1.5 border border-rule px-2.5 font-mono text-[10.5px] uppercase tracking-[0.04em] transition-colors ${
        first ? "" : "border-l-0"
      } ${active ? "bg-page text-ink" : "bg-paper text-ink-muted hover:bg-page hover:text-ink"}`}
    >
      {active ? (
        <span
          aria-hidden
          className="size-1.5 rounded-full"
          style={{ background: "var(--swatch-red)" }}
        />
      ) : null}
      {label}
    </button>
  );
}

/* ─────────────── Composer (left form) ─────────────── */

function Composer({
  invoice,
  total,
  onChange,
}: {
  invoice: Invoice;
  total: number;
  onChange: (recipe: (invoice: Invoice) => Invoice) => void;
}) {
  return (
    <div className="border-rule bg-page px-4 py-5 md:px-5 md:py-5 lg:border-r">
      <SectionHead index="00" title="Template" sku="TPL-004" />
      <TemplateStrip invoice={invoice} onChange={onChange} />

      {/* Parties — side by side */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <SectionHead index="01" title="Issuer" sku="ISR-001" />
          <PartyTable
            kind="issuer"
            party={invoice.business}
            onChange={(patch) =>
              onChange((current) => ({
                ...current,
                business: { ...current.business, ...patch },
              }))
            }
          />
        </div>
        <div>
          <SectionHead index="02" title="Recipient" sku="RCP-001" />
          <PartyTable
            kind="recipient"
            party={invoice.client}
            onChange={(patch) =>
              onChange((current) => ({ ...current, client: { ...current.client, ...patch } }))
            }
          />
        </div>
      </div>

      <SectionHead index="03" title="Company logo" sku="LOG-001" />
      <LogoBlock invoice={invoice} onChange={onChange} />

      <SectionHead index="04" title="Folio" sku="FLO-001" />
      <FolioRow invoice={invoice} onChange={onChange} />

      <SectionHead
        index="05"
        title="Line items"
        sku="LIN-001"
        total={formatMoney(total, invoice.details.currency)}
      />
      <ItemsTable invoice={invoice} onChange={onChange} />

      <SectionHead index="06" title="Notes & payment" sku="NOT-001" />
      <CellTextarea
        value={[invoice.paymentInstructions, invoice.notes].filter(Boolean).join("\n\n")}
        rows={5}
        placeholder={
          "Bank details, thank-you note, terms — anything you want under the line items."
        }
        onChange={(value) =>
          onChange((current) => ({ ...current, paymentInstructions: value, notes: "" }))
        }
        bordered
      />
    </div>
  );
}

const templateChoices: Array<{
  id: InvoiceTemplate;
  name: string;
  code: string;
  note: string;
  swatch: string;
}> = [
  {
    id: "ledger",
    name: "Mono Ledger",
    code: "BW-001",
    note: "Monospaced datasheet with tabular totals",
    swatch: "#1a1a1a",
  },
  {
    id: "atelier",
    name: "Atelier",
    code: "BW-002",
    note: "Quiet studio invoice with wide margins",
    swatch: "#f7f7f7",
  },
  {
    id: "letterhead",
    name: "Letterhead",
    code: "BW-003",
    note: "Formal centered heading and ruled sections",
    swatch: "#e8e8e8",
  },
  {
    id: "statement",
    name: "Statement",
    code: "BW-004",
    note: "Amount-first executive billing sheet",
    swatch: "#2d2d2d",
  },
  {
    id: "grid",
    name: "Grid",
    code: "BW-005",
    note: "Architectural columns and exact alignment",
    swatch: "#d9d9d9",
  },
  {
    id: "editorial",
    name: "Editorial",
    code: "BW-006",
    note: "Magazine-like hierarchy for service work",
    swatch: "#f0f0f0",
  },
  {
    id: "compact",
    name: "Compact",
    code: "BW-007",
    note: "Dense, practical, multi-line friendly",
    swatch: "#cfcfcf",
  },
  {
    id: "receipt",
    name: "Receipt",
    code: "BW-008",
    note: "Narrow receipt core on an A4 sheet",
    swatch: "#ededed",
  },
  {
    id: "executive",
    name: "Executive",
    code: "BW-009",
    note: "Boardroom clean with boxed summary",
    swatch: "#111111",
  },
  {
    id: "studio",
    name: "Studio",
    code: "BW-010",
    note: "Logo-led creative invoice, still sober",
    swatch: "#fafafa",
  },
];

function TemplateStrip({
  invoice,
  onChange,
}: {
  invoice: Invoice;
  onChange: (recipe: (invoice: Invoice) => Invoice) => void;
}) {
  const activeTemplate = invoice.template ?? "ledger";

  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {templateChoices.map((template) => {
        const active = template.id === activeTemplate;
        return (
          <button
            key={template.id}
            type="button"
            onClick={() =>
              onChange((current) => ({
                ...current,
                template: template.id,
              }))
            }
            className={`group border p-2 text-left transition-colors ${
              active
                ? "border-rule bg-stamp-yellow"
                : "border-rule-soft bg-page hover:border-rule hover:bg-paper"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className={`stamp ${template.swatch === "#111111" ? "stamp--ink" : ""}`}>
                {template.code}
              </span>
              <span
                aria-hidden
                className="h-4 w-8 border border-rule-soft"
                style={{ background: template.swatch }}
              />
            </div>
            <p className="mt-2 font-semibold text-[12px] uppercase tracking-[0.04em] text-ink">
              {template.name}
            </p>
            <p className="mt-0.5 min-h-[28px] text-[10.5px] leading-tight text-ink-muted">
              {template.note}
            </p>
            <div className="mt-2 grid grid-cols-4 gap-0.5">
              <span className="h-1 bg-[#161616]" />
              <span className="h-1 bg-[#6a6a6a]" />
              <span className="h-1 bg-[#b8b8b8]" />
              <span className="h-1 bg-[#eeeeee]" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function SectionHead({
  index,
  title,
  sku,
  total,
}: {
  index: string;
  title: string;
  sku: string;
  total?: string;
}) {
  return (
    <div className="mt-6 mb-2 first:mt-0">
      <div className="flex items-baseline justify-between gap-2 border-b border-dashed border-rule-soft pb-1">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[10px] tabular text-ink-faint">{index}</span>
          <h2 className="font-semibold text-[12px] uppercase tracking-[0.04em] text-ink">
            {title}
          </h2>
          <span className="stamp">{sku}</span>
        </div>
        {total ? (
          <span className="font-mono tabular text-[11.5px] text-ink">
            <span className="text-ink-muted">Σ </span>
            {total}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* ─────────────── Party table (Issuer / Recipient) ─────────────── */

type PartyLike = { name: string; email: string; address: string };

function PartyTable({
  kind,
  party,
  onChange,
}: {
  kind: "issuer" | "recipient";
  party: PartyLike;
  onChange: (patch: Partial<PartyLike>) => void;
}) {
  const placeholders =
    kind === "issuer"
      ? { name: "Cloudriver", email: "billing@cloudriver.ae" }
      : { name: "Northstar Labs", email: "finance@northstarlabs.com" };
  return (
    <table className="kv-table">
      <tbody>
        <KvRow label="Name">
          <input
            value={party.name}
            placeholder={placeholders.name}
            className="naked-input"
            onChange={(event) => onChange({ name: event.target.value })}
          />
        </KvRow>
        <KvRow label="Email">
          <input
            value={party.email}
            type="email"
            placeholder={placeholders.email}
            className="naked-input"
            onChange={(event) => onChange({ email: event.target.value })}
          />
        </KvRow>
        <KvRow label="Address" align="top">
          <textarea
            value={party.address}
            rows={3}
            placeholder={"Street\nCity\nCountry"}
            className="naked-textarea"
            onChange={(event) => onChange({ address: event.target.value })}
          />
        </KvRow>
      </tbody>
    </table>
  );
}

function KvRow({
  label,
  align = "center",
  children,
}: {
  label: string;
  align?: "center" | "top";
  children: ReactNode;
}) {
  return (
    <tr>
      <th scope="row" className={align === "top" ? "py-1.5 align-top" : "py-1"}>
        {label}
      </th>
      <td>{children}</td>
    </tr>
  );
}

function CellTextarea({
  value,
  onChange,
  placeholder,
  rows = 3,
  bordered,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  bordered?: boolean;
}) {
  return (
    <textarea
      value={value}
      placeholder={placeholder}
      rows={rows}
      className={`block w-full resize-none bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-faint focus:bg-stamp-yellow ${bordered ? "border border-rule px-2.5 py-1.5" : "border-0 px-2.5 py-1.5"}`}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/* ─────────────── Folio row (4 inline kv pairs) ─────────────── */

function FolioRow({
  invoice,
  onChange,
}: {
  invoice: Invoice;
  onChange: (recipe: (invoice: Invoice) => Invoice) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_88px] gap-x-5 gap-y-1">
      <FolioField label="Number">
        <input
          value={invoice.details.invoiceNumber}
          placeholder="INV-0022"
          className="naked-input font-mono"
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              details: { ...current.details, invoiceNumber: event.target.value },
            }))
          }
        />
      </FolioField>
      <FolioField label="Issued">
        <input
          type="date"
          value={invoice.details.issueDate}
          className="naked-input font-mono"
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              details: { ...current.details, issueDate: event.target.value },
            }))
          }
        />
      </FolioField>
      <FolioField label="Due">
        <input
          type="date"
          value={invoice.details.dueDate}
          className="naked-input font-mono"
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              details: { ...current.details, dueDate: event.target.value },
            }))
          }
        />
      </FolioField>
      <FolioField label="Currency">
        <select
          value={invoice.details.currency}
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              details: {
                ...current.details,
                currency: event.target.value as CurrencyCode,
              },
            }))
          }
          className="naked-input cursor-pointer appearance-none bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20viewBox=%220%200%2010%206%22%20fill=%22none%22%20stroke=%22%231a1a1a%22%20stroke-width=%221.4%22><path%20d=%22M1%201l4%204%204-4%22/></svg>')] bg-[length:9px_5.4px] bg-no-repeat bg-[position:right_2px_center] pr-5 font-mono"
        >
          {currencies.map((currency) => (
            <option key={currency.code} value={currency.code}>
              {currency.code}
            </option>
          ))}
        </select>
      </FolioField>
    </div>
  );
}

function FolioField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-dotted border-rule-soft pb-1">
      <span className="font-mono text-[10px] uppercase tracking-[0.04em] text-ink-muted">
        {label}
      </span>
      {children}
    </div>
  );
}

/* ─────────────── Logo block ─────────────── */

function LogoBlock({
  invoice,
  onChange,
}: {
  invoice: Invoice;
  onChange: (recipe: (invoice: Invoice) => Invoice) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="mt-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) =>
          void readLogo(event.currentTarget.files?.[0], (logo) =>
            onChange((current) => ({
              ...current,
              business: {
                ...current.business,
                logo,
                logoZoom: current.business.logoZoom ?? 100,
                logoX: current.business.logoX ?? 0,
                logoY: current.business.logoY ?? 0,
              },
            })),
          )
        }
      />
      {invoice.business.logo ? (
        <div className="grid grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="relative h-12 w-16 overflow-hidden border border-rule-soft bg-paper"
            aria-label="Replace logo"
          >
            <img
              src={invoice.business.logo}
              alt="Logo preview"
              className="absolute top-1/2 left-1/2 max-h-full max-w-full object-contain"
              style={logoTransformStyle(invoice)}
            />
          </button>
          <div className="flex flex-col gap-1">
            <LogoSlider
              label="Zoom"
              suffix="%"
              min={50}
              max={300}
              value={invoice.business.logoZoom ?? 100}
              onChange={(value) =>
                onChange((current) => ({
                  ...current,
                  business: { ...current.business, logoZoom: value },
                }))
              }
            />
            <LogoSlider
              label="X"
              suffix=""
              min={-100}
              max={100}
              value={invoice.business.logoX ?? 0}
              onChange={(value) =>
                onChange((current) => ({
                  ...current,
                  business: { ...current.business, logoX: value },
                }))
              }
            />
            <LogoSlider
              label="Y"
              suffix=""
              min={-100}
              max={100}
              value={invoice.business.logoY ?? 0}
              onChange={(value) =>
                onChange((current) => ({
                  ...current,
                  business: { ...current.business, logoY: value },
                }))
              }
            />
          </div>
          <div className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.04em]">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-action hover:underline"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() =>
                onChange((current) => ({
                  ...current,
                  business: { ...current.business, logoZoom: 100, logoX: 0, logoY: 0 },
                }))
              }
              className="text-ink-muted hover:text-ink"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() =>
                onChange((current) => ({
                  ...current,
                  business: { ...current.business, logo: undefined },
                }))
              }
              className="text-danger hover:underline"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-10 w-full items-center justify-center gap-2 border border-dashed border-rule-soft bg-paper font-mono text-[11px] uppercase tracking-[0.04em] text-ink-muted hover:border-rule hover:text-ink"
        >
          <ImageIcon className="size-3.5" /> Upload mark · PNG, JPG or SVG
        </button>
      )}
    </div>
  );
}

function LogoSlider({
  label,
  suffix,
  min,
  max,
  value,
  onChange,
}: {
  label: string;
  suffix: string;
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="grid grid-cols-[28px_1fr_44px] items-center gap-2">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.04em] text-ink-muted">
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
        className="accent-ink"
      />
      <span className="font-mono tabular text-right text-[10.5px] text-ink">
        {value}
        {suffix}
      </span>
    </label>
  );
}

/* ─────────────── Items table (catalog style) ─────────────── */

function ItemsTable({
  invoice,
  onChange,
}: {
  invoice: Invoice;
  onChange: (recipe: (invoice: Invoice) => Invoice) => void;
}) {
  function updateItem(id: string, patch: Partial<InvoiceItem>) {
    onChange((current) => ({
      ...current,
      items: current.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  }
  function removeItem(id: string) {
    onChange((current) => {
      if (current.items.length === 1) return current;
      return { ...current, items: current.items.filter((item) => item.id !== id) };
    });
  }
  function addItem() {
    onChange((current) => ({
      ...current,
      items: [
        ...current.items,
        { id: createId(), description: "", details: "", quantity: 1, unitPrice: 0 },
      ],
    }));
  }

  const total = invoiceTotal(invoice);

  return (
    <>
      <table className="ds-table">
        <thead>
          <tr>
            <th style={{ width: 64 }}>SKU</th>
            <th>Description</th>
            <th className="num" style={{ width: 48 }}>
              Qty
            </th>
            <th className="num" style={{ width: 84 }}>
              Unit
            </th>
            <th className="num" style={{ width: 96 }}>
              Total
            </th>
            <th style={{ width: 32 }} />
          </tr>
        </thead>
        <tbody>
          {invoice.items.map((item, index) => (
            <ItemRow
              key={item.id}
              index={index}
              item={item}
              currency={invoice.details.currency}
              canRemove={invoice.items.length > 1}
              onUpdate={updateItem}
              onRemove={removeItem}
            />
          ))}
          <tr>
            <td
              colSpan={4}
              className="bg-paper text-right font-mono text-[11px] uppercase tracking-[0.06em] text-ink-muted"
            >
              Total amount due
            </td>
            <td className="num bg-paper">
              <span className="stamp stamp--mint w-full justify-end font-semibold">
                {formatMoney(total, invoice.details.currency)}
              </span>
            </td>
            <td className="bg-paper" />
          </tr>
        </tbody>
      </table>
      <button type="button" className="dsb dsb--ghost mt-2" onClick={addItem}>
        <PlusIcon className="size-3" /> Add line
      </button>
    </>
  );
}

function ItemRow({
  index,
  item,
  currency,
  canRemove,
  onUpdate,
  onRemove,
}: {
  index: number;
  item: InvoiceItem;
  currency: CurrencyCode;
  canRemove: boolean;
  onUpdate: (id: string, patch: Partial<InvoiceItem>) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <tr className="group">
      <td>
        <span className="stamp stamp--lavender">LI-{String(index + 1).padStart(3, "0")}</span>
      </td>
      <td className="p-0">
        <input
          value={item.description}
          placeholder="Service description"
          className="block h-7 w-full border-0 bg-transparent px-2.5 text-[13px] font-medium text-ink outline-none placeholder:text-ink-faint focus:bg-stamp-yellow"
          onChange={(event) => onUpdate(item.id, { description: event.target.value })}
        />
        <input
          value={item.details ?? ""}
          placeholder="Optional supporting note"
          className="block h-6 w-full border-0 border-t border-dashed border-rule-faint bg-transparent px-2.5 text-[11.5px] text-ink-muted outline-none placeholder:text-ink-faint focus:bg-stamp-yellow"
          onChange={(event) => onUpdate(item.id, { details: event.target.value })}
        />
      </td>
      <td className="num p-0">
        <input
          type="number"
          value={item.quantity}
          className="block h-7 w-full border-0 bg-transparent px-2 text-right font-mono tabular text-[12px] text-ink outline-none focus:bg-stamp-yellow"
          onChange={(event) => onUpdate(item.id, { quantity: Number(event.target.value) })}
        />
      </td>
      <td className="num p-0">
        <input
          type="number"
          value={item.unitPrice}
          className="block h-7 w-full border-0 bg-transparent px-2 text-right font-mono tabular text-[12px] text-ink outline-none focus:bg-stamp-yellow"
          onChange={(event) => onUpdate(item.id, { unitPrice: Number(event.target.value) })}
        />
      </td>
      <td className="num font-mono tabular text-[12px] text-ink">
        {formatMoney(item.quantity * item.unitPrice, currency)}
      </td>
      <td className="p-0 text-center">
        <button
          type="button"
          aria-label="Remove line"
          onClick={() => onRemove(item.id)}
          disabled={!canRemove}
          className="flex size-7 w-full items-center justify-center text-ink-faint opacity-60 transition hover:text-danger group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <Trash2Icon className="size-3.5" />
        </button>
      </td>
    </tr>
  );
}

/* ─────────────── Preview frame ─────────────── */

function PreviewFrame({ invoice, total }: { invoice: Invoice; total: number }) {
  const itemCount = invoice.items.length;
  const template = templateChoices.find((choice) => choice.id === (invoice.template ?? "ledger"));
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    function fit() {
      if (!stage) return;
      const w = stage.clientWidth - 32;
      const h = stage.clientHeight - 32;
      if (w <= 0 || h <= 0) return;
      const s = Math.min(w / 794, h / 1123, 1);
      setScale(Math.max(s, 0.4));
    }

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(stage);
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, []);

  const zoomPct = Math.round(scale * 100);

  return (
    <section className="flex flex-col bg-paper lg:max-h-[calc(100vh-160px)]">
      {/* Top spec bar */}
      <div className="no-print flex items-center justify-between gap-3 border-b border-rule bg-page px-4 py-1.5 md:px-6">
        <div className="flex items-center gap-3 text-[10.5px] uppercase tracking-[0.04em]">
          <span className="size-1.5 rounded-full bg-action" />
          <span className="font-medium text-ink">{template?.name ?? "Template"}</span>
          <span className="text-ink-faint">·</span>
          <span className="text-ink-muted">A4 · 794×1123 · {zoomPct}%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="stamp stamp--lavender">{invoice.details.invoiceNumber || "—"}</span>
          <span className="stamp">
            {itemCount} {itemCount === 1 ? "LINE" : "LINES"}
          </span>
          <span className="stamp stamp--mint">{formatMoney(total, invoice.details.currency)}</span>
        </div>
      </div>

      {/* Sub spec bar */}
      <div className="no-print flex items-center justify-between gap-3 border-b border-rule bg-paper px-4 py-1 text-[10px] uppercase tracking-[0.06em] text-ink-muted md:px-6">
        <span>{`From: ${invoice.business.name || "—"} → To: ${invoice.client.name || "—"}`}</span>
        <span className="flex items-center gap-2">
          <span>Page 01/01</span>
          <span className="text-ink-faint">·</span>
          <span>Print-perfect</span>
        </span>
      </div>

      {/* Sheet stage — fit-to-viewport via CSS zoom, computed by ResizeObserver */}
      <div
        ref={stageRef}
        className="flex flex-1 items-start justify-center overflow-hidden p-4 md:p-6"
      >
        <div className="preview-stage" style={{ zoom: scale }}>
          <CornerMarks>
            <InvoicePreview invoice={invoice} />
          </CornerMarks>
        </div>
      </div>
    </section>
  );
}

function CornerMarks({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      <span
        aria-hidden
        className="absolute -top-2 -left-2 h-4 w-4 border-t-2 border-l-2 border-rule"
      />
      <span
        aria-hidden
        className="absolute -top-2 -right-2 h-4 w-4 border-t-2 border-r-2 border-rule"
      />
      <span
        aria-hidden
        className="absolute -bottom-2 -left-2 h-4 w-4 border-b-2 border-l-2 border-rule"
      />
      <span
        aria-hidden
        className="absolute -bottom-2 -right-2 h-4 w-4 border-b-2 border-r-2 border-rule"
      />
      {children}
    </div>
  );
}

/* ─────────────── Ledger view ─────────────── */

function Ledger({
  invoices,
  currency,
  onLoad,
  onDuplicate,
  onDelete,
}: {
  invoices: SavedInvoiceSummary[];
  currency: CurrencyCode;
  onLoad: (summary: SavedInvoiceSummary) => void;
  onDuplicate: (summary: SavedInvoiceSummary) => void;
  onDelete: (summary: SavedInvoiceSummary) => void;
}) {
  return (
    <div className="px-4 py-6 md:px-6 md:py-10">
      <div className="mx-auto max-w-[940px]">
        <p className="text-center text-[13px] text-ink-muted">
          Complete list of saved invoices, stored on this device.
        </p>
        <div className="mx-auto mt-4 inline-flex w-full flex-col items-center justify-center">
          <div className="border border-rule bg-page px-6 py-3 text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-muted">
              The Ledger
            </p>
            <p className="font-semibold text-[14px] text-ink">Saved invoices · {invoices.length}</p>
          </div>
        </div>

        <div className="mt-6">
          <p className="mb-2 text-center text-[14px] font-semibold">Saved invoices</p>
          <div className="rule-dashed mb-3" />
          {invoices.length === 0 ? (
            <p className="py-12 text-center text-[13px] text-ink-muted">
              No invoices in the ledger yet.
            </p>
          ) : (
            <table className="ds-table">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>SKU</th>
                  <th>Stock</th>
                  <th style={{ width: 140 }}>Edited</th>
                  <th className="num" style={{ width: 130 }}>
                    Total
                  </th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((summary) => (
                  <tr key={summary.id}>
                    <td>
                      <span className="stamp">{summary.invoiceNumber}</span>
                    </td>
                    <td>{summary.clientName || "—"}</td>
                    <td className="text-ink-muted">{relativeTime(summary.updatedAt)}</td>
                    <td className="num font-mono tabular">
                      {formatMoney(summary.total, currency)}
                    </td>
                    <td className="p-1.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          className="dsb dsb--xs dsb--primary"
                          onClick={() => onLoad(summary)}
                        >
                          Open <ArrowRightIcon className="size-2.5" />
                        </button>
                        <button
                          type="button"
                          className="dsb dsb--xs"
                          onClick={() => onDuplicate(summary)}
                        >
                          Dup
                        </button>
                        <button
                          type="button"
                          className="dsb dsb--xs"
                          onClick={() => onDelete(summary)}
                          aria-label={`Delete ${summary.invoiceNumber}`}
                        >
                          <XIcon className="size-2.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Footer ─────────────── */

function Footer() {
  return (
    <footer className="no-print flex flex-wrap items-center justify-between gap-3 border-t border-rule px-4 py-2 md:px-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-muted">
        © 2026, Nvoice. All rights reserved. Made for freelancers and small teams.
      </p>
      <div className="flex items-center gap-1.5">
        <span className="stamp stamp--mint">PROD</span>
        <span className="stamp">V1.0.0</span>
      </div>
    </footer>
  );
}

/* ─────────────── Helpers ─────────────── */

function logoTransformStyle(invoice: Invoice): CSSProperties {
  const zoom = invoice.business.logoZoom ?? 100;
  const x = invoice.business.logoX ?? 0;
  const y = invoice.business.logoY ?? 0;
  return {
    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${zoom / 100})`,
  };
}

async function readLogo(file: File | undefined, onLoad: (dataUrl: string) => void) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => onLoad(String(reader.result));
  reader.readAsDataURL(file);
}

function relativeTime(value: string) {
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  const minutes = Math.max(1, Math.round(diff / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function humanRelative(timestamp: number) {
  const diff = Date.now() - timestamp;
  if (diff < 4_000) return "just now";
  if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  return `${Math.round(diff / 3_600_000)}h ago`;
}
